import {
  Button,
  ClipboardIcon,
  Flex,
  FlexProps,
  IconButton,
  Image,
  LoaderCover,
  MarkdownEditor,
  Panel,
  Spacer,
  SparklesIcon,
  Text,
  Tooltip,
  toaster,
} from 'components'
import { AISummaryIcon, InformationCircleIcon, LightBulbIcon } from 'components/icons'
import { useWorkspace } from 'components/providers'
import { useState } from 'react'
import { forceDark, summaryGenerator } from 'utils'
import backdropCircles from '../../assets/backdrop-circles.svg'
import { useEvent } from '../../utils'

export default function EventIntelligence() {
  const { event } = useEvent()
  const { workspace } = useWorkspace()
  const [loading, setLoading] = useState<boolean>(false)

  async function getSummary() {
    if (!event) return
    setLoading(true)

    try {
      await summaryGenerator({
        eventId: event.id,
        useCase: workspace?.useCase || 'default',
        modelName: workspace?.aiConfig?.model,
        temperature: workspace?.aiConfig?.temperature,
      })
    } catch (error) {
      console.error('Error generating summary:', error)
    }

    setLoading(false)
  }

  return (
    <LoaderCover show={loading}>
      <Backdrop {...forceDark}>
        {event?.insights?.autoGenResults && (
          <Flex direction="column" gap={1}>
            <Flex gap={1}>
              <Flex gap={5} align="center">
                <Panel p={3} borderRadius="2xl">
                  <SparklesIcon />
                </Panel>
                <Flex direction="column">
                  <Text textStyle="h6">Your AI Event Recap is Ready!</Text>
                  <Text textStyle="bodyXSmall" opacity={0.5}>
                    Powered by Momentify Intelligence
                  </Text>
                </Flex>
              </Flex>
              <Tooltip
                content={
                  <Flex direction="column" gap={1} align="center">
                    <Text textStyle="bodySmall">Powered by Momentify Intelligence</Text>
                    <Text textStyle="bodySmall" fontWeight="normal">
                      This datapoint is powered by our AI intelligence that helps provide more
                      powerful insights for your events.
                    </Text>
                  </Flex>
                }
              >
                <InformationCircleIcon fontSize="md" opacity={0.5} />
              </Tooltip>
              <Spacer />
              <Button onClick={getSummary} loading={loading}>
                Regenerate AI Summary
              </Button>
            </Flex>
            <Flex direction="column" gap={1} pl={{ base: 4, md: 16 }}>
              <Flex gap={2}>
                <Tooltip content="Event Summary">
                  <AISummaryIcon mt={3} />
                </Tooltip>
                <MarkdownEditor readOnly value={event.insights.autoGenResults?.summary!} />
                <IconButton
                  aria-label="Copy event summary"
                  icon={<ClipboardIcon type="outline" size="sm" />}
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    handleCopy(event?.insights?.autoGenResults?.summary, 'Event summary')
                  }
                />
              </Flex>
              <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
                <Flex gap={1}>
                  <Tooltip content="Moments Performance">
                    <LightBulbIcon fontSize="lg" mt={3} />
                  </Tooltip>
                  <MarkdownEditor
                    readOnly
                    value={event.insights.autoGenResults?.momentsPerformance!}
                  />
                </Flex>
                <IconButton
                  aria-label="Copy moments performance"
                  icon={<ClipboardIcon type="outline" size="sm" />}
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    handleCopy(
                      event?.insights?.autoGenResults?.momentsPerformance,
                      'Moments performance'
                    )
                  }
                />
                <Flex gap={1} align="flex-start">
                  <Tooltip content="Recommendations">
                    <SparklesIcon fontSize="lg" mt={3} />
                  </Tooltip>
                  <MarkdownEditor
                    readOnly
                    value={event.insights.autoGenResults?.recommendations!}
                  />
                  <IconButton
                    aria-label="Copy recommendations"
                    icon={<ClipboardIcon type="outline" size="sm" />}
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      handleCopy(
                        event?.insights?.autoGenResults?.recommendations,
                        'Recommendations'
                      )
                    }
                  />
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Backdrop>
    </LoaderCover>
  )
}

interface BackdropProps extends FlexProps {}

function Backdrop({ children, ...rest }: BackdropProps) {
  return (
    <Flex
      minHeight="100%"
      flex={1}
      borderRadius="20px"
      bg="linear-gradient(91deg, #1637AB -0.83%, #E83A5A 93.64%), rgba(255, 255, 255, 0.44)"
    >
      <Flex position="absolute" height="100%" width="100%" overflow="hidden">
        <Image src={backdropCircles} position="absolute" top="0px" left="0" width="81px" />

        <Image src={backdropCircles} position="absolute" bottom="36px" left="-23px" width="196px" />
      </Flex>
      <Flex zIndex={1} p={children ? 6 : 0} {...rest} direction="column">
        {children}
      </Flex>
    </Flex>
  )
}

async function handleCopy(text: string | undefined, label: string) {
  if (!text) return

  try {
    await navigator.clipboard.writeText(text)
    toaster.create({ title: `${label} copied to clipboard`, type: 'success' })
  } catch (error) {
    console.error('Error copying text:', error)
    toaster.create({ title: 'Unable to copy to clipboard', type: 'error' })
  }
}
